import React, { useState, useEffect } from "react";
import {
  FaEnvelope,
  FaFilter,
  FaSearch,
  FaTrash,
  FaEye,
  FaCheckCircle,
  FaClock,
  FaReply,
  FaArchive,
  FaBan,
  FaChartBar,
  FaSpinner,
} from "react-icons/fa";
import { contactService } from "./contact.service";
import { ContactQueryParams } from "./contact.types";

/* ================= STAT CARD ================= */
const StatCard = ({ icon, title, value, color }) => (
  <div className={`relative p-6 text-white transition-all duration-300 rounded-3xl shadow-xl hover:shadow-2xl hover:-translate-y-1 ${color}`}>
    <div className="absolute inset-0 rounded-3xl bg-white/10 backdrop-blur-lg"></div>
    <div className="relative flex items-center gap-5">
      <div className="flex items-center justify-center w-16 h-16 text-2xl rounded-2xl bg-white/20 backdrop-blur-md">
        {icon}
      </div>
      <div>
        <p className="text-sm tracking-widest uppercase opacity-90">{title}</p>
        <h3 className="text-4xl font-bold">{value}</h3>
      </div>
    </div>
  </div>
);

/* ================= STATUS BADGE ================= */
const StatusBadge = ({ status }) => {
  const statusConfig = {
    new: { color: "bg-blue-100 text-blue-700", label: "New", icon: <FaClock /> },
    read: { color: "bg-gray-100 text-gray-700", label: "Read", icon: <FaEye /> },
    responded: { color: "bg-green-100 text-green-700", label: "Responded", icon: <FaReply /> },
    archived: { color: "bg-purple-100 text-purple-700", label: "Archived", icon: <FaArchive /> },
    spam: { color: "bg-red-100 text-red-700", label: "Spam", icon: <FaBan /> },
  };

  const config = statusConfig[status] || statusConfig.new;

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full ${config.color}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

/* ================= MAIN COMPONENT ================= */
export default function ContactMessages() {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    page: 1,
    limit: 20,
  });

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [filters]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = new ContactQueryParams(
        filters.status || null,
        filters.search,
        filters.page,
        filters.limit
      );
      const response = await contactService.getMessages(params);
      if (response.status) {
        setMessages(response.messages);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await contactService.getStats();
      if (response.status) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await contactService.updateStatus(id, { status: newStatus });
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    try {
        console.log("a:::::::", selectedMessages);
      await contactService.bulkUpdateStatus({
        ids: selectedMessages,
        status: newStatus,
      });
      setSelectedMessages([]);
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error("Failed to bulk update:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      try {
        await contactService.deleteMessage(id);
        fetchMessages();
        fetchStats();
      } catch (error) {
        console.error("Failed to delete message:", error);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedMessages.length} message(s)?`)) {
      try {
        await contactService.bulkDelete(selectedMessages);
        setSelectedMessages([]);
        fetchMessages();
        fetchStats();
      } catch (error) {
        console.error("Failed to bulk delete:", error);
      }
    }
  };

  const toggleSelectMessage = (id) => {
    setSelectedMessages((prev) =>
      prev.includes(id) ? prev.filter((msgId) => msgId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedMessages.length === messages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(messages.map((msg) => msg.id));
    }
  };

  return (
    <div className="min-h-screen p-4">
      {/* HERO HEADER */}
      <div className="flex items-center justify-between mb-12">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-14 h-14 text-white rounded-2xl shadow-md bg-gradient-to-br from-[#6a0dad] to-[#9f5bd8]">
            <FaEnvelope className="text-xl" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Contact Messages</h1>
            <p className="text-sm text-gray-500">Manage and respond to customer inquiries</p>
          </div>
        </div>
      </div>

      {/* STATS GRID */}
      {stats && (
        <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={<FaEnvelope />}
            title="Total Messages"
            value={stats.total_messages || 0}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
          />
          <StatCard
            icon={<FaClock />}
            title="New Messages"
            value={stats.new_messages || 0}
            color="bg-gradient-to-br from-yellow-500 to-yellow-600"
          />
          <StatCard
            icon={<FaCheckCircle />}
            title="Responded"
            value={stats.responded_messages || 0}
            color="bg-gradient-to-br from-green-500 to-green-600"
          />
          <StatCard
            icon={<FaChartBar />}
            title="This Month"
            value={stats.month_messages || 0}
            color="bg-gradient-to-br from-purple-500 to-purple-600"
          />
        </div>
      )}

      {/* FILTERS & ACTIONS */}
      <div className="p-6 mb-8 bg-white shadow-xl rounded-3xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Search */}
          <div className="relative flex-1">
            <FaSearch className="absolute text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
            <input
              type="text"
              placeholder="Search by name, email, or message..."
              className="w-full py-3 pl-12 pr-4 border rounded-xl focus:ring-2 focus:ring-[#6a0dad] focus:outline-none"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FaFilter className="absolute text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
            <select
              className="py-3 pl-12 pr-8 border rounded-xl focus:ring-2 focus:ring-[#6a0dad] focus:outline-none appearance-none"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="responded">Responded</option>
              <option value="archived">Archived</option>
              <option value="spam">Spam</option>
            </select>
          </div>

          {/* Bulk Actions */}
          {selectedMessages.length > 0 && (
            <div className="flex gap-2">
              <select
                className="px-4 py-3 text-sm border rounded-xl focus:ring-2 focus:ring-[#6a0dad]"
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkStatusChange(e.target.value);
                    e.target.value = "";
                  }
                }}
              >
                <option value="">Bulk Actions ({selectedMessages.length})</option>
                <option value="read">Mark as Read</option>
                <option value="responded">Mark as Responded</option>
                <option value="archived">Archive</option>
                <option value="spam">Mark as Spam</option>
              </select>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-3 text-white transition bg-red-500 rounded-xl hover:bg-red-600"
              >
                <FaTrash />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MESSAGES TABLE */}
      <div className="overflow-hidden bg-white shadow-xl rounded-3xl">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <FaSpinner className="text-4xl animate-spin text-[#6a0dad]" />
          </div>
        ) : messages.length === 0 ? (
          <div className="py-20 text-center text-gray-500">
            <FaEnvelope className="mx-auto mb-4 text-6xl opacity-20" />
            <p className="text-lg">No messages found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-[#6a0dad] to-[#9f5bd8] text-white">
                <tr>
                  <th className="p-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedMessages.length === messages.length}
                      onChange={toggleSelectAll}
                      className="w-5 h-5 cursor-pointer"
                    />
                  </th>
                  <th className="p-4 text-left">Name</th>
                  <th className="p-4 text-left">Email</th>
                  <th className="p-4 text-left">Message</th>
                  <th className="p-4 text-left">Status</th>
                  <th className="p-4 text-left">Date</th>
                  <th className="p-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((message, idx) => (
                  <tr
                    key={message.id}
                    className={`border-b transition hover:bg-gray-50 ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedMessages.includes(message.id)}
                        onChange={() => toggleSelectMessage(message.id)}
                        className="w-5 h-5 cursor-pointer"
                      />
                    </td>
                    <td className="p-4 font-medium text-gray-900">{message.name}</td>
                    <td className="p-4 text-gray-600">{message.email}</td>
                    <td className="p-4 text-sm text-gray-600 max-w-xs truncate">
                      {message.message}
                    </td>
                    <td className="p-4">
                      <StatusBadge status={message.status} />
                    </td>
                    <td className="p-4 text-sm text-gray-500">
                      {new Date(message.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <select
                          className="px-3 py-2 text-xs border rounded-lg focus:ring-2 focus:ring-[#6a0dad]"
                          value={message.status}
                          onChange={(e) => handleStatusChange(message.id, e.target.value)}
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="responded">Responded</option>
                          <option value="archived">Archived</option>
                          <option value="spam">Spam</option>
                        </select>
                        <button
                          onClick={() => handleDelete(message.id)}
                          className="p-2 text-red-500 transition rounded-lg hover:bg-red-50"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}